import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import { maxLength, required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeWelcomePacket } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface WelcomePacketFormData {
    student: string;
    formId: string;

    patientName: string;
    dob: string;
    age: string;
    address: string;
    homePhone: string;

    cell: string;
    work: string;
    email: string;
    maritalStatus: string;
    gender: string;

    employerOrSchool: string;
    isSpecEd: boolean;
    isIep: boolean;
    emergencyContact: string;
    emergencyPhone: string;

    referredBy: string;
    speciality: string;
    phone: string;

    parentOneName: string;
    parentOnePhoneOne: string;
    parentOnePhoneTwo: string;
    parentOnePhoneThree: string;
    parentOneEmail: string;

    parentTwoName: string;
    parentTwoPhoneOne: string;
    parentTwoPhoneTwo: string;
    parentTwoPhoneThree: string;
    parentTwoEmail: string;
}

interface WelcomePacketFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<WelcomePacketFormData, WelcomePacketFormProps>;

const validators: Validators<WelcomePacketFormData> = {
    patientName: [maxLength({ max: 255 })],
    dob: [maxLength({ max: 255 })],
    age: [maxLength({ max: 255 })],
    address: [maxLength({ max: 255 })],
    homePhone: [maxLength({ max: 255 })],

    cell: [maxLength({ max: 255 })],
    work: [maxLength({ max: 255 })],
    email: [maxLength({ max: 255 })],
    maritalStatus: [maxLength({ max: 255 })],
    gender: [maxLength({ max: 255 })],

    employerOrSchool: [maxLength({ max: 255 })],
    //isSpecEd: [maxLength({ max: 255 })],
    //isIep: [maxLength({ max: 255 })],
    emergencyContact: [maxLength({ max: 255 })],
    emergencyPhone: [maxLength({ max: 255 })],

    referredBy: [maxLength({ max: 255 })],
    speciality: [maxLength({ max: 255 })],
    phone: [maxLength({ max: 255 })],

    parentOneName: [maxLength({ max: 255 })],
    parentOnePhoneOne: [maxLength({ max: 255 })],
    parentOnePhoneTwo: [maxLength({ max: 255 })],
    parentOnePhoneThree: [maxLength({ max: 255 })],
    parentOneEmail: [maxLength({ max: 255 })],

    parentTwoName: [maxLength({ max: 255 })],
    parentTwoPhoneOne: [maxLength({ max: 255 })],
    parentTwoPhoneTwo: [maxLength({ max: 255 })],
    parentTwoPhoneThree: [maxLength({ max: 255 })],
    parentTwoEmail: [maxLength({ max: 255 })],
};

const WelcomePacketForm: React.FunctionComponent<InjectedProps & WelcomePacketFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & WelcomePacketFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Welcome Packet & Contact Information</h2>
            <div>Last updated 3/2/2021</div>
            <div className={classNames(styles.info)}>
                <p>Dear Prospective Patient/Client,</p>
                <p>
                    We are excited that you are considering engaging Manhattan Psychology Group, PC for ABA treatment.
                    Since 2008, Dr. Rosenthal and his team have been providing expert psychological and behavioral care.
                </p>
                <p>Here are some key facts that make our ABA program unique:</p>
                <p>
                    1. <b>Experience</b> – Our BCBAs might also be special education teachers, school psychologists,
                    neuroscientists, social workers or clinical psychologists. This gives us a depth and breadth of
                    knowledge far beyond ABA alone, as team leaders, supervisors and direct service care providers.
                </p>
                <p>
                    2. <b>Family Focus</b> – our ABA clinicians are excellent at creating family-oriented, data-driven
                    programs with real-world meaning. We focus on teaching independent life skills and reducing
                    problematic behaviors, while also providing support to the entire family and across settings.
                </p>
                <p>
                    3. <b>Naturalistic</b> – our ABA programming meets the needs of the learner and changes over time as
                    he/she builds mastery. Our BCBAs balance teaching discrete and real-world skills in a way that
                    harnesses the therapist-patient relationship and external rewards.
                </p>
                <p>
                    Please read, complete, initial and/or sign each page in this intake packet and fax or email all the
                    pages back to us (ABA@manhattanpsychologygroup.com or 212-500-0007) prior to your first appointment:
                </p>
                <p>We look forward to working together!</p>
                <p>Sincerely,</p>
                <table className="signature-table">
                    <tr>
                        <td className="signature-cell">
                            <img
                                className="signature-image"
                                src="https://aba-development.s3.amazonaws.com/sign.png"
                                alt="Signature Joshua"
                            />
                            <p>
                                <b>Joshua Rosenthal, PsyD</b>
                                <br />
                                Clinical Psychologist
                                <br />
                                President, Manhattan Psychology Group, PC
                                <br />
                                Director of Child & Adolescent Treatment
                            </p>
                        </td>
                        <td className="signature-cell">
                            <img
                                className="signature-image"
                                style={{ width: 160 }}
                                src="https://aba-development.s3.amazonaws.com/sign-two.png"
                                alt="Signature Ariel"
                            />
                            <p>
                                <b>Ariel Kornblum, PsyD, BCBA, LBA</b>
                                <br />
                                Board Certified Behavior Analyst
                                <br />
                                Clinical Psychologist
                                <br />
                                Director of ABA Services
                            </p>
                        </td>
                    </tr>
                </table>
            </div>
            <h2>Contact Information</h2>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={17}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="patientName"
                type="text"
                validate={validators.patientName}
                wrapperConfig={{
                    label: 'Patient Name:',
                }}
            />
            <FieldDate
                name="dob"
                type="text"
                date={true}
                validate={validators.dob}
                wrapperConfig={{
                    label: 'Date of Birth:',
                }}
            />
            <FieldInput
                name="age"
                type="text"
                validate={validators.age}
                wrapperConfig={{
                    label: 'Age:',
                }}
            />

            <FieldInput
                name="address"
                type="text"
                validate={validators.address}
                wrapperConfig={{
                    label: 'Address:',
                }}
            />

            <FieldInput
                name="homePhone"
                type="text"
                validate={validators.homePhone}
                wrapperConfig={{
                    label: 'Home Phone:',
                }}
            />
            <FieldInput
                name="cell"
                type="text"
                validate={validators.cell}
                wrapperConfig={{
                    label: 'Cell:',
                }}
            />
            <FieldInput
                name="work"
                type="text"
                validate={validators.work}
                wrapperConfig={{
                    label: 'Work:',
                }}
            />

            <FieldInput
                name="email"
                type="text"
                validate={validators.email}
                wrapperConfig={{
                    label: 'Email:',
                }}
            />
            <FieldInput
                name="maritalStatus"
                type="text"
                validate={validators.maritalStatus}
                wrapperConfig={{
                    label: 'Marital Status:',
                }}
            />
            <FieldInput
                name="gender"
                type="text"
                validate={validators.gender}
                wrapperConfig={{
                    label: 'Gender:',
                }}
            />

            <FieldInput
                name="employerOrSchool"
                type="text"
                validate={validators.employerOrSchool}
                wrapperConfig={{
                    label: 'Employer or School & Grade:',
                }}
            />

            <FieldInput
                name="isSpecEd"
                type="checkbox"
                validate={validators.isSpecEd}
                wrapperConfig={{
                    label: 'Spec Ed?',
                    classes: {
                        inputWrapper: 'customCheckbox customCheckboxWide ',
                    },
                }}
            />
            <FieldInput
                name="isIep"
                type="checkbox"
                validate={validators.isIep}
                wrapperConfig={{
                    label: 'IEP?',
                    classes: {
                        inputWrapper: 'customCheckbox customCheckboxWide ',
                    },
                }}
            />
            <FieldInput
                name="emergencyContact"
                type="text"
                validate={validators.emergencyContact}
                wrapperConfig={{
                    label: 'Emergency Contact / Relationship:',
                }}
            />
            <FieldInput
                name="emergencyPhone"
                type="text"
                validate={validators.emergencyPhone}
                wrapperConfig={{
                    label: 'Phone:',
                }}
            />

            <FieldInput
                name="referredBy"
                type="text"
                validate={validators.referredBy}
                wrapperConfig={{
                    label: 'Referred by:',
                }}
            />
            <FieldInput
                name="speciality"
                type="text"
                validate={validators.speciality}
                wrapperConfig={{
                    label: 'Specialty:',
                }}
            />
            <FieldInput
                name="phone"
                type="text"
                validate={validators.phone}
                wrapperConfig={{
                    label: 'Phone:',
                }}
            />

            <div className={classNames(styles.descr)}>
                <b>
                    If patient is less than 18 years old, please complete provide the parents’ or legal guardians’
                    information below:
                </b>
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="parentOneName"
                    type="text"
                    validate={validators.parentOneName}
                    wrapperConfig={{
                        label: 'Name:',
                    }}
                />

                <div className={classNames(styles.checkBoxLabel)}>Phones:</div>
                <FieldInput
                    name="parentOnePhoneOne"
                    type="text"
                    validate={validators.parentOnePhoneOne}
                    wrapperConfig={{
                        label: '(H):',
                    }}
                />
                <FieldInput
                    name="parentOnePhoneTwo"
                    type="text"
                    validate={validators.parentOnePhoneTwo}
                    wrapperConfig={{
                        label: '(W):',
                    }}
                />
                <FieldInput
                    name="parentOnePhoneThree"
                    type="text"
                    validate={validators.parentOnePhoneThree}
                    wrapperConfig={{
                        label: '(C):',
                    }}
                />
                <FieldInput
                    name="parentOneEmail"
                    type="text"
                    validate={validators.parentOneEmail}
                    wrapperConfig={{
                        label: 'Email:',
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <FieldInput
                    name="parentTwoName"
                    type="text"
                    validate={validators.parentTwoName}
                    wrapperConfig={{
                        label: 'Name:',
                    }}
                />
                <div className={classNames(styles.checkBoxLabel)}>Phones:</div>
                <FieldInput
                    name="parentTwoPhoneOne"
                    type="text"
                    validate={validators.parentTwoPhoneOne}
                    wrapperConfig={{
                        label: '(H):',
                    }}
                />
                <FieldInput
                    name="parentTwoPhoneTwo"
                    type="text"
                    validate={validators.parentTwoPhoneTwo}
                    wrapperConfig={{
                        label: '(W):',
                    }}
                />
                <FieldInput
                    name="parentTwoPhoneThree"
                    type="text"
                    validate={validators.parentTwoPhoneThree}
                    wrapperConfig={{
                        label: '(C):',
                    }}
                />

                <FieldInput
                    name="parentTwoEmail"
                    type="text"
                    validate={validators.parentTwoEmail}
                    wrapperConfig={{
                        label: 'Email:',
                    }}
                />
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<WelcomePacketFormData, WelcomePacketFormProps>({
    form: 'welcome-packet-form',
    enableReinitialize: true,
    onSubmit: async (data: WelcomePacketFormData, dispatch: Dispatch<any>, props: WelcomePacketFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeWelcomePacket({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<WelcomePacketFormData, WelcomePacketFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(WelcomePacketForm);
